import type { Scan } from "@hobbyshopify/backend-client";
import { newScanAttemptId } from "@hobbyshopify/backend-client";
import { ChangeNotifier } from "subtree.js/core";
import { type CardImages, pairFiles } from "../primitives/scan-files";
import { initialScanProgress, type ScanProgress } from "../primitives/scan-progress";
import type { StoreRepository } from "../repositories/store-repository";

// How many scans are in flight at once. Each is ~20s of mostly waiting on
// OpenAI/eBay/SportsCardsPro, so the limit is about being a good citizen to
// those APIs rather than about local load.
const CONCURRENCY = 10;

export type BulkItemStatus = "queued" | "scanning" | "error";

export type BulkItem = {
  readonly attemptId?: string;
  readonly id: string;
  readonly file: File;
  /** Back of the same card, when the filenames pair up (see pairFiles). */
  readonly backFile: File | null;
  readonly previewUrl: string;
  readonly status: BulkItemStatus;
  readonly progress: ScanProgress;
  readonly error: string | null;
};

export type BulkCounts = { queued: number; scanning: number; error: number };

/** Where a scan lands: the shop, and the counter it was taken at. */
export type ScanScope = { shopId: string; locationId: string | null };

/** Runs one scan for the queue; the op behind it is `runScan`. */
export type ScanRunner = (
  scope: ScanScope,
  images: CardImages & { attemptId?: string },
  onProgress: (progress: ScanProgress) => void,
  signal: AbortSignal,
) => Promise<Scan>;

/**
 * Queues card images and scans them a few at a time. A service: it lives for
 * the app, so a batch keeps going while the reviewer opens a scan and comes
 * back. The drafts controller mirrors it into page state.
 *
 * Adding files starts the batch as soon as the store is known; there is nothing
 * to confirm, the drop *is* the instruction. A finished scan leaves the queue
 * and is handed to `onScanned`: it is a draft from that moment on, so the page
 * shows the real draft row rather than a second copy of it here. A failure
 * stays in the queue as `error`, never re-queued automatically (a card that
 * always fails would spin forever), and is retried per row.
 */
export class BulkScanQueue extends ChangeNotifier {
  readonly #storeRepository: StoreRepository;
  readonly #scan: ScanRunner;
  readonly #onScanned: ((scan: Scan) => void) | undefined;
  readonly #concurrency: number;
  readonly #previewUrl: (file: File) => string;
  readonly #revokePreview: (url: string) => void;

  #items: readonly BulkItem[] = [];
  #running = false;
  // Set by Stop, cleared by the next drop or retry: without it the auto-start
  // would immediately restart everything Stop just parked.
  #paused = false;
  #abort: AbortController | null = null;
  #nextId = 1;
  #scopes = new Map<string, ScanScope>();

  constructor(o: {
    storeRepository: StoreRepository;
    scan: ScanRunner;
    onScanned?: (scan: Scan) => void;
    concurrency?: number;
    /** Object URLs for thumbnails; injectable because tests run without a DOM. */
    previewUrl?: (file: File) => string;
    revokePreview?: (url: string) => void;
  }) {
    super();
    this.#storeRepository = o.storeRepository;
    this.#scan = o.scan;
    this.#onScanned = o.onScanned;
    this.#concurrency = o.concurrency ?? CONCURRENCY;
    this.#previewUrl = o.previewUrl ?? ((file) => URL.createObjectURL(file));
    this.#revokePreview = o.revokePreview ?? ((url) => URL.revokeObjectURL(url));
    // A drop before the store resolved starts once it has.
    this.#storeRepository.addListener(this.#maybeStart);
  }

  get items(): readonly BulkItem[] {
    return this.#items;
  }

  get running(): boolean {
    return this.#running;
  }

  get paused(): boolean {
    return this.#paused;
  }

  get counts(): BulkCounts {
    const by = (s: BulkItemStatus) => this.#items.filter((i) => i.status === s).length;
    return { queued: by("queued"), scanning: by("scanning"), error: by("error") };
  }

  addFiles(files: Iterable<File>): void {
    const images = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) return;
    const next: BulkItem[] = pairFiles(images).map(({ front, back }) => ({
      id: `bulk-${this.#nextId++}`,
      attemptId: newScanAttemptId(),
      file: front,
      backFile: back,
      previewUrl: this.#previewUrl(front),
      status: "queued",
      progress: initialScanProgress(),
      error: null,
    }));
    this.#paused = false;
    this.#items = [...this.#items, ...next];
    this.notifyListeners();
    this.#maybeStart();
  }

  /** Parks the queue. Scans already in flight still complete server-side; only the reading stops. */
  stop(): void {
    this.#abort?.abort();
    this.#abort = null;
    this.#running = false;
    this.#paused = true;
    this.#items = this.#items.map((item) => (item.status === "scanning" ? { ...item, status: "queued" } : item));
    this.notifyListeners();
  }

  resume(): void {
    this.#paused = false;
    this.notifyListeners();
    this.#maybeStart();
  }

  /** Puts one failed card back in the queue. */
  retry(id: string): void {
    this.#paused = false;
    const item = this.#items.find((i) => i.id === id);
    this.#patch(id, {
      status: "queued",
      error: null,
      ...(item?.progress.status === "error" ? { attemptId: newScanAttemptId() } : {}),
      progress: initialScanProgress(),
    });
    this.#maybeStart();
  }

  remove(id: string): void {
    const item = this.#items.find((i) => i.id === id);
    if (!item) return;
    this.#scopes.delete(id);
    this.#revokePreview(item.previewUrl);
    this.#items = this.#items.filter((i) => i.id !== id);
    this.notifyListeners();
  }

  override dispose(): void {
    this.#storeRepository.removeListener(this.#maybeStart);
    this.#abort?.abort();
    super.dispose();
  }

  readonly #maybeStart = (): void => {
    if (this.#running || this.#paused) return;
    const { shopId, locationId } = this.#storeRepository;
    if (shopId === null) return;
    const pending = this.#items.filter((i) => i.status === "queued");
    if (pending.length === 0) return;
    // Captured once: a batch started at one counter finishes there, even if the merchant switches mid-way.
    void this.#run({ shopId, locationId }, pending);
  };

  async #run(scope: ScanScope, pending: readonly BulkItem[]): Promise<void> {
    const controller = new AbortController();
    this.#abort = controller;
    this.#running = true;
    this.notifyListeners();

    // A fixed pool rather than chunked batches: batching would idle the whole
    // pool waiting for the slowest scan in each group, and scan durations vary
    // a lot. Here a finished slot immediately takes the next file.
    let cursor = 0;
    const worker = async () => {
      while (cursor < pending.length && !controller.signal.aborted) {
        const item = pending[cursor++];
        await this.#scanOne(scope, item, controller.signal);
      }
    };
    await Promise.all(Array.from({ length: Math.min(this.#concurrency, pending.length) }, worker));

    if (this.#abort === controller) {
      this.#abort = null;
      this.#running = false;
      this.notifyListeners();
      // Anything queued meanwhile (a second drop, a retry) gets its turn.
      this.#maybeStart();
    }
  }

  async #scanOne(scope: ScanScope, item: BulkItem, signal: AbortSignal): Promise<void> {
    if (!this.#scopes.has(item.id)) {
      this.#scopes.set(item.id, scope);
    }

    this.#patch(item.id, { status: "scanning", error: null, progress: initialScanProgress() });
    try {
      const scan = await this.#scan(
        this.#scopes.get(item.id) ?? scope,
        { front: item.file, back: item.backFile, attemptId: item.attemptId },
        (progress) => {
          if (!signal.aborted) this.#patch(item.id, { progress });
        },
        signal,
      );
      if (signal.aborted) return; // Stop already put it back in the queue.
      this.#onScanned?.(scan);
      this.remove(item.id);
    } catch (error) {
      if (signal.aborted) return;
      // One bad card must not stop the batch: it is recorded and the pool moves on.
      this.#patch(item.id, { status: "error", error: error instanceof Error ? error.message : "Scan failed" });
    }
  }

  #patch(id: string, updates: Partial<BulkItem>): void {
    if (!this.#items.some((i) => i.id === id)) return;
    this.#items = this.#items.map((item) => (item.id === id ? { ...item, ...updates } : item));
    this.notifyListeners();
  }
}
